import { Dropdown } from '@entur/dropdown'
import { VariantType } from '@entur/form'
import React, { useState, useEffect } from 'react'
import { Category, Company, FetchState, TicketType } from '../../api-clients/api-types'
import { useApiOnLoad } from '../../api-clients/client-hooks'
import { getProducts, getTicketTypes } from '../../api-clients/eba-clients'
import { ValidationFormField, nullValue } from '../../utils/form-utils'
import { capitalize } from '../../utils/string-utils'

export const ticketTypeCategoryFormField = (
  value: string = nullValue,
  required = false
): ValidationFormField<string> => {
  return {
    value,
    displayValue: 'Ikke valgt',
    variant: '',
    feedback: '',
    fieldName: 'ticketType',
    displayName: 'Rettighetstype',
    validation: {
      required,
    },
  }
}

type DropdownItemType = {
  value: string | undefined
  label: string
}

const prefixSeparator = '-'

const categoryValuePrefix = 'C'
const categoryToDropdownItemType = (category: Category): DropdownItemType => {
  return {
    label: `Årsrettighet, ${capitalize(category.name)}`,
    value: `${categoryValuePrefix}${prefixSeparator}${category.id}`,
  }
}

const ticketTypeApprenticeValuePrefix = 'TTA'
const ticketTypePathValuePrefix = 'TTP'
export const ticketTypeToDropdownItemType = (ticketType: TicketType): DropdownItemType => {
  const prefix = ticketType.canHaveStationPairs ? ticketTypePathValuePrefix : ticketTypeApprenticeValuePrefix
  return {
    label: `${capitalize(ticketType.description)}`,
    value: `${prefix}${prefixSeparator}${ticketType.id}`,
  }
}

export enum UpdateTypes {
  UNKNOWN,
  CATEGORY,
  TICKET_TYPE_APPRENTICE,
  TICKET_TYPE_PATH,
}

type SelectedUpdateType = {
  type: UpdateTypes
  value: string
}
export const getSelectedUpdateTypeByValue = (selectedValue: string): SelectedUpdateType => {
  const split = selectedValue.split(prefixSeparator)
  const prefix = split[0]
  const value = split[1]

  switch (prefix) {
    case categoryValuePrefix:
      return {
        type: UpdateTypes.CATEGORY,
        value,
      }

    case ticketTypeApprenticeValuePrefix:
      return {
        type: UpdateTypes.TICKET_TYPE_APPRENTICE,
        value,
      }

    case ticketTypePathValuePrefix:
      return {
        type: UpdateTypes.TICKET_TYPE_PATH,
        value,
      }

    default:
      return {
        type: UpdateTypes.UNKNOWN,
        value: nullValue,
      }
  }
}

type TicketTypeCategoryDropdownProps = {
  formField: ValidationFormField<string>
  company: Company
  update: (value: ValidationFormField<string>) => void
  ticketType?: 'trainee' | 'path' | 'yearly'
}
const TicketTypeCategoryDropdown: React.FC<TicketTypeCategoryDropdownProps> = (props) => {
  const [variant, setVariant] = useState(props.formField.variant)
  const [feedback, setFeedback] = useState(props.formField.feedback)
  const selectedItem =
    props.formField.value !== nullValue
      ? {
          label: props.formField.displayValue || '',
          value: props.formField.value,
        }
      : null
  const [ticketTypeResponse] = useApiOnLoad(getTicketTypes, {})
  const [categoryResponse] = useApiOnLoad<Array<Category>>(getProducts, {
    id: props.company.companyId.toString() || '',
  })

  const update = getSelectedUpdateTypeByValue(props.formField.value)

  const undefinedLabel: Array<DropdownItemType> = [{ label: 'Ikke valgt', value: nullValue }]
  useEffect(() => {
    if (categoryResponse.status === FetchState.SUCCESS) {
      const isUpdatingCategory = update.type === UpdateTypes.CATEGORY
      const validCategory = categoryResponse.data.find(
        (product) => parseInt(product.id, 10) === parseInt(update.value, 10)
      )
      if (isUpdatingCategory && !validCategory && props.formField.value !== nullValue) {
        setVariant('info')
        setFeedback(`${props.formField.displayValue} er ikke tilgjengelig for ${props.company.name}`)
      } else {
        setFeedback(``)
      }
    }
  }, [categoryResponse, props, update])

  const traineeTicketTypes =
    ticketTypeResponse.status === FetchState.SUCCESS
      ? ticketTypeResponse.data.items
          .filter((ticketType) => {
            if (props.ticketType === 'trainee') {
              return !ticketType.canHaveStationPairs
            }
            if (props.ticketType === 'path') {
              return ticketType.canHaveStationPairs
            }
            return true
          })
          .flatMap<DropdownItemType>(ticketTypeToDropdownItemType)
      : []

  const productsAndTicketTypes =
    categoryResponse.status === FetchState.SUCCESS
      ? categoryResponse.data
          .flatMap<DropdownItemType>(categoryToDropdownItemType)
          .concat(traineeTicketTypes)
          .concat(undefinedLabel)
      : [...undefinedLabel]

  const dropdownItems: Array<DropdownItemType> = props.ticketType ? traineeTicketTypes : productsAndTicketTypes

  return (
    <Dropdown<string | undefined>
      style={{ cursor: 'pointer' }}
      label={props.formField.displayName}
      variant={variant as VariantType}
      feedback={feedback}
      items={() => dropdownItems}
      selectedItem={selectedItem}
      onChange={(e) => {
        if (e) {
          props.update({ ...props.formField, value: e.value || nullValue, displayValue: e.label })
        }
      }}
    />
  )
}

export default TicketTypeCategoryDropdown
